import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PageMeta } from "@/components/PageMeta";
import { ContentPageShell, Prose } from "@/components/ContentPageShell";

const FAQ = [
  {
    q: "Co jsou Speciální tipy?",
    a: "Speciální tipy jsou dlouhodobé otázky k celému turnaji — například vítěz soutěže nebo tým s nejvíce vstřelenými góly. Tipujete je jednou před startem a body získáte, jakmile je znám správný výsledek.",
  },
  {
    q: "Kdy se Speciální tipy uzamykají?",
    a: "Standardně výkopem prvního zápasu turnaje. Správce turnaje může u konkrétní otázky nastavit vlastní termín uzávěrky. Po uzamčení už odpověď nelze změnit.",
  },
  {
    q: "Kolik bodů Speciální tipy dávají?",
    a: "Výchozí hodnota je 2 body za správnou odpověď (u některých otázek 1 bod). Admin ligy může počet bodů u každé otázky upravit nebo otázku úplně vypnout.",
  },
  {
    q: "Musí je liga mít zapnuté?",
    a: "Ne. Speciální tipy jsou volitelné. Admin ligy je zapíná v nastavení ligy a vybírá, které otázky se ve své lize objeví.",
  },
];

export default function SpecialBets() {
  return (
    <>
      <PageMeta
        title="Speciální tipy — pravidla a příklady | Tipomatic"
        description="Jak fungují Speciální tipy v Tipomaticu: dlouhodobé turnajové otázky, uzávěrka výkopem prvního zápasu, bodování a konkrétní příklady otázek."
        path="/special-bets"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          })}
        </script>
      </Helmet>
      <ContentPageShell>
        <h1 className="text-3xl md:text-4xl font-bold mb-6">Speciální tipy</h1>
        <Prose>
          <p>
            <strong>Speciální tipy</strong> jsou samostatný typ tipování vedle výsledků
            jednotlivých zápasů. Místo skóre odpovídáte na dlouhodobé otázky k celému turnaji —
            kdo vyhraje Ligu mistrů, který tým nastřílí nejvíc gólů nebo kolik gólů padne
            v nejgólovějším zápase. Odpovídáte jednou před startem a body dostanete, jakmile je
            výsledek jistý.
          </p>

          <h2>Jak Speciální tipy fungují</h2>
          <ol>
            <li>Správce turnaje připraví sadu otázek pro daný turnaj (např. Liga mistrů 2026/27).</li>
            <li>
              Admin ligy v nastavení ligy Speciální tipy <strong>zapne</strong> a vybere, které
              otázky se v jeho lize objeví. Zároveň může u každé změnit počet bodů.
            </li>
            <li>
              Hráči odpovídají v lize na záložce <strong>Speciální tipy</strong> — u týmových
              otázek vybírají tým, u číselných zadávají číslo.
            </li>
            <li>
              Po uzávěrce se odpovědi zamknou. Když správce zadá správnou odpověď, body se
              automaticky připočtou do tabulky ligy.
            </li>
          </ol>

          <h2>Uzávěrka</h2>
          <p>
            Většina otázek se uzamyká <strong>výkopem prvního zápasu turnaje</strong>. U konkrétní
            otázky lze nastavit vlastní datum a čas uzávěrky — hodí se u otázek, které se týkají
            až vyřazovací fáze. Po uzávěrce už odpověď nelze upravit ani doplnit; tipy zadané
            později se neuloží.
          </p>

          <h2>Bodování</h2>
          <p>
            Za správnou odpověď získáte plný počet bodů, za špatnou nula — nic mezi tím se
            nepočítá. Výchozí hodnota je <strong>2 body</strong> (u méně náročných otázek, jako je
            finalista soutěže, například 1 bod). Admin ligy může u každé otázky nastavit vlastní
            hodnotu. Body ze Speciálních tipů se sčítají s body za zápasy i skupinové tipy a
            promítají se do celkového pořadí — detailní pravidla najdete na stránce{" "}
            <Link to="/scoring-rules">Bodovací pravidla</Link>.
          </p>

          <h2>Konkrétní příklady otázek</h2>
          <p>Takto vypadá sada Speciálních tipů, kterou používáme u Ligy mistrů:</p>
          <ul>
            <li>
              <strong>🥇 Vítěz celé soutěže</strong> — tým, který zvedne pohár. Odpověď: tým.
            </li>
            <li>
              <strong>⭐ Finalista soutěže</strong> — tým, který se probojuje do finále. Odpověď: tým.
            </li>
            <li>
              <strong>🏆 Nejlepší tým základní části</strong> — první tým tabulky po odehrání
              ligové fáze. Odpověď: tým.
            </li>
            <li>
              <strong>📉 Nejhorší tým základní části</strong> — poslední tým tabulky. Odpověď: tým.
            </li>
            <li>
              <strong>⚽ Tým s nejvíce vstřelenými góly</strong> v základní části. Odpověď: tým.
            </li>
            <li>
              <strong>🧤 Tým s nejvíce obdrženými góly</strong> v základní části. Odpověď: tým.
            </li>
            <li>
              <strong>💣 Největší rozdíl ve skóre zápasu</strong> — např. výsledek 1:5 znamená
              rozdíl 4. Odpověď: číslo.
            </li>
            <li>
              <strong>🎯 Nejvíc gólů v jednom zápase</strong> — součet branek obou týmů, např.
              3:2 je 5. Odpověď: číslo.
            </li>
          </ul>

          <h2>Příklad výpočtu</h2>
          <p>Liga má zapnuté čtyři otázky po 2 bodech. Po turnaji vyjde:</p>
          <ul>
            <li>
              <strong>Petr</strong> trefil vítěze soutěže i nejlepší tým základní části →{" "}
              <strong>4 body</strong>.
            </li>
            <li>
              <strong>Marie</strong> trefila jen největší rozdíl ve skóre (5) → <strong>2 body</strong>.
            </li>
            <li>
              <strong>Honza</strong> netrefil nic → <strong>0 bodů</strong>.
            </li>
          </ul>
          <p>
            Tyto body se přičtou k bodům za zápasy. Speciální tipy tak často rozhodnou o pořadí
            na špici i v lize, kde mají hráči po zápasech skoro stejně.
          </p>

          <h2>Tipy pro admina ligy</h2>
          <ul>
            <li>Zapněte Speciální tipy <strong>před startem turnaje</strong>, ať mají hráči čas odpovědět.</li>
            <li>
              Nechte otázek raději méně (4–8). Dlouhý seznam odrazuje a část hráčů ho nevyplní.
            </li>
            <li>
              Chcete, aby Speciální tipy měly větší váhu? Zvyšte body u těžších otázek (vítěz
              soutěže) a nechte 1 bod u snadných.
            </li>
            <li>
              Máte nápad na vlastní otázku? <Link to="/contact">Napište nám</Link> a přidáme ji.
            </li>
          </ul>

          <h2>Časté dotazy</h2>
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3>{f.q}</h3>
              <p>{f.a}</p>
            </div>
          ))}

          <p className="pt-6">
            Další čtení: <Link to="/how-it-works">Jak to funguje</Link> ·{" "}
            <Link to="/scoring-rules">Bodovací pravidla</Link> · <Link to="/blog">Blog</Link>
          </p>
        </Prose>
      </ContentPageShell>
    </>
  );
}
